import React, { useEffect, useState } from 'react';
import { getYear } from 'date-fns';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogClose } from '../ui/dialog';
import RotateLeftIcon from '../../assets/Reload.png';
import Logo from '../../assets/Logo.svg';
import { RxCross1 } from 'react-icons/rx';

import config from '../../config';
import { useDebugMode } from '../../context/appContext';
import { useResetQuestionnaire } from '../../hooks/useResetQuestionnaire';
import { AccordionLearnMore } from './AccordionLearnMore';
import { useNavigate } from 'react-router-dom';
import { useSelectedPatient } from '../../hooks/patient.hooks';
import { useMode } from '../../hooks/useMode';

interface MenuProps {
  open?: boolean;
  onClose?: () => void;
  openWhyPrepareConsultation?: boolean;
  openWhatIsAldebaran?: boolean;
}

export const Menu = ({
  open,
  onClose,
  openWhyPrepareConsultation,
  openWhatIsAldebaran,
}: MenuProps) => {
  const [debugModeVisible, setDebugModeVisible] = useState(false);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [debugMode, setDebugMode] = useDebugMode();
  const reset = useResetQuestionnaire();
  const navigate = useNavigate();
  const mode = useMode();
  const { data: patient } = useSelectedPatient();

  const isLightRestart = shiftHeld && (config.env.dev || config.env.staging);

  const handleNewQuestionnaire = () => {
    console.log('User reset questionnaire. Light restart:', isLightRestart);
    onClose?.();
    if (isLightRestart) {
      reset();
    } else {
      reset({ clearStorage: true });
    }
  };

  const handleDebugMode = () => {
    setDebugMode(!debugMode);
    onClose?.();
  };

  useEffect(() => {
    if (config.debug.panel) {
      setDebugModeVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(true);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent className="md:h-full md:flex md:flex-col sm:h-auto sm:mt-0 nsm:w-[452px] nsm:mt-0 mt-4">
        <div className="flex items-start justify-between rounded-t p-4">
          <div className="w-8 h-8"></div>
          <div className="flex items-center gap-2 mt-6 w-full items-center justify-center">
            <img src={Logo} alt="Aldebaran logo" />
          </div>
          <DialogClose className="h-8 rounded-lg cursor-pointer focus:ring-0 focus:outline-hidden focus:border-transparent">
            <RxCross1
              className="h-8 w-8 rounded-lg hover:bg-gray-200 p-1 cursor-pointer text-gray-600 stroke-[0.5]"
              onClick={onClose}
            />
          </DialogClose>
        </div>
        <div className="flex-1 overflow-auto px-8 overflow-y-auto no-scrollbar">
          <div className="flex flex-col space-y-2 h-full pb-2">
            <div className="flex flex-col items-center justify-between h-full gap-4">
              <div className="w-full">
                <AccordionLearnMore
                  openWhyPrepareConsultation={openWhyPrepareConsultation}
                  openWhatIsAldebaran={openWhatIsAldebaran}
                />
              </div>
              <div className="w-full pb-8">
                <div className="flex justify-center mb-4">
                  <Button
                    onClick={handleNewQuestionnaire}
                    variant="ctaPrimary"
                    size="xl"
                    className={`font-normal ${
                      isLightRestart ? '!bg-blue-600' : ''
                    }`}
                  >
                    <img
                      src={RotateLeftIcon}
                      alt="Nouveau questionnaire"
                      className="w-5 h-5 mr-2 ml-1"
                    />
                    <p className="text-sm w-full">
                      {isLightRestart
                        ? 'DEV / STAGING - Light restart'
                        : 'Recommencer un questionnaire'}
                    </p>
                  </Button>
                </div>

                <div className="flex flex-col items-center gap-6">
                  <span className="w-full text-center text-gray-500 text-2xs leading-[150%]">
                    © {getYear(new Date())} Aldebaran <br />
                    <a
                      href="https://aldebaran.care"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 text-2xs underline"
                    >
                      www.aldebaran.care
                    </a>{' '}
                    <br />
                    70 Rue du Docteur Yersin 59120 LOOS
                  </span>
                </div>

                {mode !== 'INVITATION' && patient ? (
                  <button
                    className="text-gray-500 text-xs underline cursor-pointer w-full text-center mt-6"
                    onClick={() => {
                      navigate('/unsub-confirm?source=menu');
                      onClose?.();
                    }}
                  >
                    Se désinscrire des communications
                  </button>
                ) : (
                  <></>
                )}

                {debugModeVisible && (
                  <div className="flex flex-col items-center sticky bottom-0 w-full bg-white py-4 gap-2">
                    <Button
                      variant="ctaSecondary"
                      size="md"
                      onClick={handleDebugMode}
                    >
                      {debugMode ? (
                        <p>Désactiver le mode debug</p>
                      ) : (
                        <p>Activer le mode debug</p>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
